import styles from "./styles.module.scss";
import {
  Button,
  CustomSelect,
  Input,
  OptionType,
  initOptionType,
  optionTypeSchema,
} from "components";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { objectiveOptions } from "./utils";
import { Routes } from "router";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { EnvelopeIcon, PrecisionLogoMain } from "assets";
import { SignUpPayload } from "hooks";

interface SignupData {
  email: string;
  password: string;
  confirmPassword: string;
  objective: OptionType;
  agreement: boolean;
}

const initSignup: SignupData = {
  email: "",
  password: "",
  confirmPassword: "",
  objective: initOptionType,
  agreement: false,
};

// Custom password validation method
yup.addMethod(yup.string, "noEmailInPassword", function (emailRef, message) {
  return this.test("no-email-in-password", message, function (value) {
    const { path, createError, parent } = this;
    const email = parent[emailRef];
    if (email && value) {
      const emailParts = email.split(/[@._]/);
      emailParts.pop()
      for (const part of emailParts) {
        const mailPart = part.toLowerCase().replaceAll(/\d/g, "");
        if (value.toLowerCase().includes(mailPart)) {
          return createError({ path, message });
        }
      }
    }
    return true;
  });
});

// Extend Yup's string schema to include the new method
declare module "yup" {
  interface StringSchema {
    noEmailInPassword(emailRef: string, message: string): this;
  }
}

const schema = yup.object({
  email: yup.string().email("Enter a valid email").required("Required"),
  password: yup
    .string()
    .required("Required")
    .min(8, "Password should be at least 8 characters long")
    .noEmailInPassword(
      "email",
      "Password should not contain parts of the email address"
    )
    .matches(/[A-Z]/, "Password should contain an uppercase character")
    .matches(/[a-z]/, "Password should contain an lowercase character")
    .matches(/[0-9]/, "Password should contain at least one number")
    .matches(
      /[@#&$]/,
      "Password should contain at least special character (e.g. @, #, &, $)"
    ),
  confirmPassword: yup
    .string()
    .required("Required")
    .oneOf([yup.ref("password")], "Passwords must match"),
  objective: optionTypeSchema(),
  agreement: yup
    .boolean()
    .required("You have to agree to the terms and conditions to continue")
    .oneOf([true], "You have to agree to the terms and conditions to continue"),
});

interface SignupProps {
  submit: (data: SignUpPayload) => void;
  clear: boolean;
}

const SignupUI: React.FC<SignupProps> = ({ submit, clear }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    reset,
  } = useForm<SignupData>({
    resolver: yupResolver(schema),
    mode: "onTouched",
    defaultValues: initSignup,
  });

  useEffect(() => {
    reset(initSignup);
  }, [clear]);

  const onSubmit: SubmitHandler<SignupData> = (data) => {
    const submitData: SignUpPayload = {
      email: data.email.toLowerCase().trim(),
      objective: data.objective.value,
      password1: data.password,
      password2: data.confirmPassword,
    };
    submit(submitData);
  };

  return (
    <div className="">
      <div className="bg-[#26279A] h-[320px] pt-[40px] px-5 lg:hidden flex flex-col">
        <PrecisionLogoMain className='w-[180px] flex-shrink-0' />
        <h1 className='text-[23px] font-bold mt-5 text-white w-fit lg:hidden flex'>
          Get Started With Precision
        </h1>
        <p className="text-white text-sm mt-4 lg:text-center lg:hidden flex">
          Create your account to get started with smarter hiring
        </p>
      </div>
      <div className="lg:w-full w-[90%] mx-auto lg:mt-0 -mt-[80px] rounded-xl lg:p-0 p-4 bg-[#E8F3FF] sm:mb-0 mb-8">
        <h1 className='lg:text-[27px] text-xl font-bold lg:mt-9 mt-6 text-[#012169] w-fit lg:mx-auto lg:block hidden lg:text-center'>
          Get Started With Precision
        </h1>
        <p className="text-[#475467] text-sm mt-4 lg:text-center lg:flex hidden">
          Create your account to get started with smarter hiring
        </p>
        <form className="lg:mt-7" noValidate>
          <Input
            label="Email"
            placeholder="email@example.com"
            type="email"
            required
            validatorMessage={errors.email?.message}
            name="email"
            register={register}
            value={watch("email")}
            parentClassName="[&>label]:text-[#344054] [&>label]:font-light !mb-2"
            Icon={EnvelopeIcon}
            iconClassName="!w-4.5 !h-4.5 left-4 !top-11"
            className="!pl-11 !bg-white !border-[#EAECF0] !shadow-none"
            showRequired
          />
          <Input
            label="Password"
            placeholder="Enter password"
            type="password"
            required
            validatorMessage={errors.password?.message}
            name="password"
            register={register}
            value={watch("password")}
            parentClassName="[&>label]:text-[#344054] [&>label]:font-light"
            className=" !bg-white !border-[#EAECF0] !shadow-none"
            iconClassName="!w-4 !h-4 !top-11"
            showRequired
          />
          <Input
            label="Confirm Password"
            placeholder="Confirm password"
            type="password"
            required
            validatorMessage={errors.confirmPassword?.message}
            name="confirmPassword"
            register={register}
            value={watch("confirmPassword")}
            parentClassName="[&>label]:text-[#344054] [&>label]:font-light"
            className=" !bg-white !border-[#EAECF0] !shadow-none"
            iconClassName="!w-4 !h-4 !top-11"
            showRequired
          />
          <CustomSelect
            label="Primary objective for using Precision in the next 30 days:"
            placeholder="Select option"
            options={objectiveOptions}
            onChange={(val) => setValue("objective", val, { shouldValidate: true })}
            validatorMessage={errors.objective?.value?.message?.toString() ?? ""}
            name={"objective"}
            value={watch("objective")}
            parentClassName="[&>label]:text-[#344054] [&>label]:font-light !mt-6 !mb-2 !shadow-none"
            inputClass="!min-h-[44px] !bg-white rounded-md placeholder:!text-[#98A2B3]"
            dataTestID="objective"
            menuPlacement="auto"
            showRequired
          />
          <div className='mt-5'>
            <label className="!text-[#566B9B] !text-xs relative inline-flex items-center">
              <div className="relative inline-flex">
                <input
                  {...register("agreement", {
                    required: true,
                  })}
                  name="agreement"
                  checked={watch("agreement")}
                  type="checkbox"
                  className="
          peer mr-2 w-4 h-4 appearance-none rounded-[3px] border border-[#566B9B] 
          checked:bg-transparent cursor-pointer flex-shrink-0 mt-1.5"
                />
                <span
                  className="pointer-events-none absolute left-[5.5px] top-[8px] w-[5px] h-[9px] rotate-45 border-r-2 border-b-2 border-[#012169] opacity-0 peer-checked:opacity-100"
                />
              </div>

              <span>
                By creating an account you agree to the
                {" "} <Link target="_blank" to={Routes.termsOfUse} className="font-bold">
                  terms of use
                </Link>{" "} and our{" "}
                <Link target="_blank" to={Routes.privacyPolicy} className="font-bold">
                  privacy policy
                </Link>.
              </span>
            </label>
            {errors.agreement?.message && (
              <p className={styles.errorMsg}>{errors.agreement?.message}</p>
            )}
          </div>
          <Button
            className='!bg-[#012169] !text-[15px] !border-none w-full text-white !hover:bg-[#012169] !hover:opacity-60 mt-6 mb-3 !py-3 !rounded-[5px]'
            type="fill"
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
          >
            Create Account
          </Button>
        </form>
        <p className='text-[#2B4682] mx-auto w-fit pt-1 pb-8'>
          Got An Account? <Link to={Routes.signin} className="text-[#012169] font-semibold">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export { SignupUI };
